import React from 'react'
import { Box, Typography, makeStyles, Theme } from '@material-ui/core'
import HotelIcon from '@material-ui/icons/Hotel'

import { SeasonProgressMetrics } from '../types/leasingSeason.types'
import { SeasonLeaseUpBar } from './SeasonLeaseUpBar'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        minWidth: 0,
        gap: theme.spacing(1.5),
        backgroundColor: theme.palette.grey[50],
        borderRadius: theme.spacing(1),
        padding: theme.spacing(0.5, 1),
    },
    tiles: {
        display: 'flex',
        alignItems: 'stretch',
        flexShrink: 0,
        gap: theme.spacing(0.5),
    },
    tile: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        minWidth: 0,
        padding: theme.spacing(0.25, 1.25),
        backgroundColor: theme.palette.background.paper,
        borderRadius: theme.spacing(0.75),
    },
    tileLabel: {
        fontSize: '0.7rem',
        fontWeight: 600,
        color: theme.palette.text.secondary,
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
    },
    tileValue: {
        fontSize: '1.15rem',
        fontWeight: 700,
        lineHeight: 1.2,
        color: theme.palette.text.primary,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(0.5),
        whiteSpace: 'nowrap',
    },
    capacity: {
        fontSize: '0.85rem',
        fontWeight: 500,
        color: theme.palette.text.secondary,
    },
    bedIcon: {
        fontSize: 18,
        color: '#6b7280',
    },
    divider: {
        alignSelf: 'stretch',
        width: 1,
        flexShrink: 0,
        backgroundColor: theme.palette.divider,
        margin: theme.spacing(0.5, 0),
    },
    barSection: {
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: theme.spacing(0.5),
    },
    barCaptions: {
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        minWidth: 0,
    },
    barCaption: {
        fontSize: '0.85rem',
        fontWeight: 600,
        color: theme.palette.text.secondary,
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
    },
}))

interface SeasonProgressHeaderProps {
    metrics: SeasonProgressMetrics
    lastYearPercent?: number | null
}

export const SeasonProgressHeader: React.FC<SeasonProgressHeaderProps> = ({
    metrics,
    lastYearPercent = null,
}) => {
    const classes = useStyles()
    const paceValue = `${
        metrics.paceDelta >= 0 ? '+' : ''
    }${metrics.paceDelta.toFixed(1)}%`
    const daysLeftLabel = `${metrics.daysRemaining} day${
        metrics.daysRemaining === 1 ? '' : 's'
    } left`
    const bedsToGoalLabel = `${metrics.bedsToGoal} bed${
        metrics.bedsToGoal === 1 ? '' : 's'
    } to goal`

    return (
        <Box className={classes.root}>
            <Box className={classes.tiles}>
                <Box className={classes.tile}>
                    <Typography className={classes.tileLabel}>
                        Occupied
                    </Typography>
                    <Typography className={classes.tileValue}>
                        <HotelIcon className={classes.bedIcon} />
                        <span>
                            {metrics.leasedCount}
                            <span className={classes.capacity}>
                                {' '}
                                / {metrics.totalCapacity}
                            </span>
                        </span>
                    </Typography>
                </Box>
                <Box className={classes.tile}>
                    <Typography className={classes.tileLabel}>
                        New tenants
                    </Typography>
                    <Typography className={classes.tileValue}>
                        {metrics.signedNew}
                    </Typography>
                </Box>
                <Box className={classes.tile}>
                    <Typography className={classes.tileLabel}>
                        Renewals
                    </Typography>
                    <Typography className={classes.tileValue}>
                        {metrics.signedRenewals}
                    </Typography>
                </Box>
            </Box>

            <Box className={classes.divider} />

            <Box className={classes.barSection}>
                <Box className={classes.barCaptions}>
                    <Typography className={classes.barCaption}>
                        {daysLeftLabel}
                    </Typography>
                    <Typography className={classes.barCaption}>
                        {bedsToGoalLabel}
                    </Typography>
                </Box>
                <SeasonLeaseUpBar
                    percentLeased={metrics.percentLeased}
                    expectedPercent={metrics.expectedPercent}
                    lastYearPercent={lastYearPercent}
                    compact
                    hideMarkerLabels
                    paceCaption={`${paceValue} vs pace`}
                    paceOn={metrics.onPace}
                />
            </Box>
        </Box>
    )
}
